/**
 * Playground API shapes — aligned with progsity-backend `playground.routes.ts` + `playground.service.ts`.
 */

import type { PlaygroundLanguage } from "./config/languages";

export type { PlaygroundLanguage };

/** POST /v1/playground/run */
export type RunCodeInput = {
  languageId: string;
  sourceCode: string;
  stdin?: string;
  compilerOptions?: string;
  /** A version **id** from the contract — never a toolchain string or a flag. */
  languageVersion?: string;
};

/** What actually ran, resolved server-side (PG-IDE01, EX-PUB04B). */
export type RuntimeVersionView = {
  id: string;
  label: string;
  /** The *active engine's* toolchain for this row, never the other one's. */
  toolchain: string;
  /** The pin the server could not honour, so the UI can say so out loud. */
  fellBackFrom: string | null;
  /**
   * The retired pin this version superseded.
   *
   * Optional on the wire so a client that outlives a rollback still renders.
   * Distinct from `fellBackFrom`: one is "your version is gone", the other is
   * "we moved Python 3.8 to 3.11 and your code ran on 3.11".
   */
  supersededFrom?: string | null;
};

/**
 * What happened to a run, in vocabulary no engine owns (`EX-PUB04B`).
 *
 * The field to branch on, and since 2026-09-06 the only one: the retired
 * `statusId` that sat beside it through the engine swap left the wire once this
 * client stopped reading it (ADR-0058, the 2026-08-18 decision point 4).
 */
export type PlaygroundRunOutcome =
  | "success"
  | "runtime_error"
  | "compilation_error"
  | "time_limit_exceeded"
  | "memory_limit_exceeded"
  /** The runner's 1 MB output cap stopped the program (`PG-OUT01`). */
  | "output_limit_exceeded"
  | "internal_error";

/** Which engine served a request. One value since the Judge0 deletion (ADR-0058). */
export type PlaygroundEngine = "runner";

export type ExecutionResult = {
  stdout: string | null;
  stderr: string | null;
  compileOutput: string | null;
  exitCode: number | null;
  /** Optional on the wire so a client that outlives a deploy still renders. */
  outcome?: PlaygroundRunOutcome;
  /** Which engine ran it, so a support question has one answer. */
  engine?: PlaygroundEngine;
  /**
   * The retired compatibility number. No longer sent and read by nothing
   * (ADR-0058); declared optional only so a cached response still type-checks.
   */
  statusId?: number;
  status: string;
  timeMs: number | null;
  memoryKb: number | null;
  outputTruncated: boolean;
  truncatedStreams: ExecutionOutputStream[];
  /**
   * Optional on the wire so a client that outlives a rollback still renders.
   * The UI shows this rather than the version it thinks it asked for.
   */
  version?: RuntimeVersionView;
};

export type ExecutionOutputStream = "stdout" | "stderr" | "compileOutput";

/** Owner snippet — GET by Mongo id (auth) + list/create/update responses */
export type SavedSnippet = {
  _id: string;
  userId: string;
  title: string;
  description: string | null;
  languageId: string;
  /** The author's pin, verbatim; `null` when they never made one. */
  languageVersion?: string | null;
  /** The pin resolved server-side; `fellBackFrom` when it no longer exists. */
  version?: RuntimeVersionView | null;
  sourceCode: string;
  stdin: string | null;
  isPublic: boolean;
  isPasswordProtected: boolean;
  shareSlug: string | null;
  forkedFrom: string | null;
  /** Set when snippet is in a folder (IDE Pro). */
  folderId?: string | null;
  /**
   * Owner-only analytics (`PG-ANALYTICS01`). Server-owned totals, present on
   * owner payloads and absent from every public and cross-user response.
   * Optional on the wire so a client that outlives a rollback still renders.
   */
  viewCount?: number;
  forkCount?: number;
  /**
   * The owner's embed opt-in (`PG-EMBED01`, ADR-0050). Owner payloads only:
   * the public share read and the embed read carry neither the flag nor the
   * allowlist. Optional on the wire so a client that outlives a rollback
   * still renders, and absent reads as "not embeddable", which is what it was.
   */
  embed?: SnippetEmbedSettings;
  createdAt?: string;
  updatedAt?: string;
};

/** `allowedOrigins` empty means any parent may frame the embed. */
export type SnippetEmbedSettings = {
  enabled: boolean;
  allowedOrigins: string[];
};

/**
 * One row of an owner's snippet history (`PG-IDE05`, `docs/24` §4.1).
 *
 * A version is the snippet **as it stood before a save that changed its
 * content**. The list carries no `sourceCode`: it is a picker, and the source
 * arrives from the single-version read for the row the owner selects.
 */
export type SnippetVersionSummary = {
  _id: string;
  snippetId: string;
  title: string;
  description: string | null;
  languageId: string;
  languageVersion: string | null;
  /** Characters of source, so a row can show its size without carrying it. */
  sourceChars: number;
  createdAt?: string;
};

/** One version with the content, for the diff and the restore. */
export type SnippetVersion = SnippetVersionSummary & {
  sourceCode: string;
  stdin: string | null;
};

export type SnippetVersionsResponse = {
  versions: SnippetVersionSummary[];
};

export type SnippetAuthor = {
  displayName: string;
  /** Public profile slug when available (for linking to /u/[username]). */
  username?: string | null;
};

/** GET by share slug (public read) */
export type PublicSharedSnippet = {
  _id: string;
  author: SnippetAuthor;
  title: string;
  description: string | null;
  languageId: string;
  languageVersion?: string | null;
  version?: RuntimeVersionView | null;
  sourceCode: string;
  stdin: string | null;
  isPublic: boolean;
  isPasswordProtected: boolean;
  shareSlug: string | null;
  forkedFrom: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type SaveSnippetInput = {
  title: string;
  description?: string | null;
  languageId: string;
  /** `null` clears the pin back to the language's current default. */
  languageVersion?: string | null;
  sourceCode: string;
  stdin?: string | null;
  /** Write-only; the API returns only `isPasswordProtected`. */
  password?: string | null;
};

export type UpdateSnippetInput = {
  title?: string;
  description?: string | null;
  languageId?: string;
  languageVersion?: string | null;
  sourceCode?: string;
  stdin?: string | null;
  /** Write-only; `null` removes protected-share access. */
  password?: string | null;
  /** Move between folders; `null` = uncategorized (IDE Pro). */
  folderId?: string | null;
  /**
   * Embed opt-in (`PG-EMBED01`). Partial on purpose: revoking is
   * `{ enabled: false }` and does not restate the origin list.
   */
  embed?: { enabled?: boolean; allowedOrigins?: string[] };
};

export type ShareSnippetInput = {
  /** Omit to preserve current protection; `null` removes it. */
  password?: string | null;
};

export type SharedSnippetAccessGrant = {
  granted: true;
  expiresAt: string;
};

export type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

export type MySnippetsResponse = {
  snippets: SavedSnippet[];
  meta: PaginationMeta;
};

export type JudgeStatusWire = {
  ok: boolean;
  version?: string;
  message?: string;
};

export type PlaygroundHealth = {
  ok: boolean;
  /** Which engine this health is about. Absent on a pre-`EX-PUB04B` API. */
  engine?: PlaygroundEngine;
  judge: JudgeStatusWire;
  languagesConfigured: number;
};

export type LanguagesListResponse = {
  languages: PlaygroundLanguage[];
};

/** GET /v1/playground/runtimes — versions plus what has confirmed them. */
export type RuntimeDiscoveryState = "fresh" | "stale" | "unverified";
export type RuntimeVerification = "verified" | "mismatch" | "unverified";

export type RuntimeCatalogueVersion = {
  id: string;
  label: string;
  toolchain: string;
  isDefault: boolean;
};

export type RuntimeCatalogueLanguage = {
  id: string;
  name: string;
  defaultVersionId: string;
  versions: RuntimeCatalogueVersion[];
  allowedCompilerOptions: string[];
  verification: RuntimeVerification;
  reportedToolchain?: string;
};

export type RuntimeCatalogue = {
  schemaVersion: number;
  /** Which engine these rows describe. Absent on a pre-`EX-PUB04B` API. */
  engine?: PlaygroundEngine;
  discovery: {
    state: RuntimeDiscoveryState;
    checkedAt: string | null;
    verifiedLanguages: number;
    declaredLanguages: number;
  };
  languages: RuntimeCatalogueLanguage[];
};

/** GET /v1/playground/me/plan */
export type IdePlanResponse = {
  plan: "free" | "pro" | "internal";
  reason: string;
  unlockedVia?: string;
};

/** POST /v1/playground/explain */
export type ExplainCodeInput = {
  sourceCode: string;
  languageId: string;
  /** Fresh `pgex_<UUIDv4>` per deliberate request; reused only for retries. */
  idempotencyKey: string;
};

export type CodeExplainLineBreakdown = {
  lineRange: string;
  explanation: string;
};

export type CodeExplainResult = {
  summary: string;
  lineByLineBreakdown: CodeExplainLineBreakdown[];
  improvements: string[];
};

/** POST /v1/playground/fix-error (`PG-AI02`) */
export type FixErrorInput = {
  languageId: string;
  sourceCode: string;
  /** Only a compile or runtime error is eligible; the server checks again. */
  outcome: "compilation_error" | "runtime_error";
  /** The language the answer must be written in, echoed back and validated. */
  locale: "en" | "bn";
  compileOutput?: string | null;
  stderr?: string | null;
  stdout?: string | null;
  /** Fresh `pgfx_<UUIDv4>` per deliberate request; reused only for retries. */
  idempotencyKey: string;
};

export type CodeFixStep = {
  title: string;
  detail: string;
};

export type CodeFixResult = {
  locale: "en" | "bn";
  cause: string;
  explanation: string;
  steps: CodeFixStep[];
  pitfalls: string[];
  /**
   * The student's own source with the smallest edit that fixes the error, or
   * `null` when the model offered none or the one it offered changed too much
   * to be a fix (the server drops it; a rewrite is never returned).
   */
  patchedSource: string | null;
};

/** POST /v1/playground/fix-error/feedback (`PG-AI02`) */
export type FixErrorFeedbackInput = {
  idempotencyKey: string;
  helpful?: boolean;
  patchAccepted?: true;
};

/** MongoDB ObjectId string (24 hex) — disambiguated from share slugs on the backend. */
export const SNIPPET_OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/;

/** Mirrors backend `STDIN_MAX` (SavedSnippet.model.ts + run schema) — enforce client-side too. */
export const STDIN_MAX_LENGTH = 10_000;
